/**
 * DI-3-D — the board's ETA: per stage `rate × remaining` (story §2.1; locked
 * eta-is-rate-times-remaining). Pure.
 *
 * The rate is the stage's own measured rate on THIS set once it has one
 * sample (hash: rows hashed / elapsed after ≥ 50 rows; metadata: rows sent /
 * elapsed after the first batch; upload: bytes / elapsed after the first MiB),
 * else the record defaults below. The four server stages (extract → align →
 * match → geometry) are ONE job with no progress inside it: they get ONE ETA
 * from the geometry default per image, counted down from the run's
 * `startedAt`. A set's ETA is the sum of its remaining stages; the drop's is
 * the max over its sets. A blocked stage has no time — the tile says why.
 */

/**
 * The defaults before a set has a sample of its own. Each number names the
 * record it comes from; change one only with a new measurement.
 */
export const DEFAULT_RATES = {
  /**
   * Files hashed per second in the browser — parse + MD5 + payload SHA-256
   * of the 527 F-M400 files took 58 s on 8 lanes (M2; DI-3-A §0 "measured
   * timings (M2)", Intake Workflows v2 §6 "Verified") → 527 / 58 ≈ 9.1.
   */
  hashFilesPerS: 527 / 58,
  /**
   * Seconds per point for the metadata round trip —
   * DI-3-P1 (BE #476, 2026-09-29): a 527-point create on the dev cluster
   * 9.2 s after the batched lookup → ≈ 0.017 s/point; the server side of the
   * round trip.
   * (Was 0.2 s/point before the batched lookup: DI-3-A §10, AC-9, 103 867 +
   * 5 018 ms for 527 points on the dev DB.)
   */
  metadataSPerPoint: 0.017,
  /**
   * Seconds per image for the ONE server job (extract → align → match →
   * geometry): the F-M400 geometry job ran 1.4 s, ≈ 2 s at record version 3,
   * for 527 images (DI-3-A §0 "measured timings (M2)") → 2 / 527 ≈ 3.8 ms.
   */
  geometrySPerImage: 2 / 527,
  /**
   * Upload bits per second — ASSUMED 20 Mbit/s (a hotel uplink; Intake
   * Workflows v2 §6 "Assumed"; DI-3-C departure 6). DI-3-F: this is only the
   * number before the first MiB went up — from then on the set's own
   * measured rate (bytes sent / time since its first block) replaces it, and
   * a set still queued borrows the rate the uplink showed so far. The LAN
   * number of the Azurite check is recorded in DI-3-F's record, not here.
   */
  uploadBitsPerS: 20_000_000,
} as const;

/** A stage's own sample needs this much before it replaces the default. */
export const MIN_SAMPLE = {
  /** rows hashed */
  hashRows: 50,
  /** rows sent (the first batch) */
  metadataRows: 1,
  /** bytes uploaded (1 MiB) */
  uploadBytes: 1024 * 1024,
} as const;

export interface Sample {
  /** Units done so far (rows, bytes). */
  done: number;
  /** Seconds since the stage started. */
  elapsedS: number;
}

export interface Rate {
  /** Units per second. */
  perS: number;
  /** True once the set's own sample replaced the record default. */
  measured: boolean;
}

function measured(
  sample: Sample | null | undefined,
  min: number,
  fallbackPerS: number,
): Rate {
  if (sample && sample.done >= min && sample.elapsedS > 0)
    return { perS: sample.done / sample.elapsedS, measured: true };
  return { perS: fallbackPerS, measured: false };
}

/** Rows hashed per second. */
export function hashRate(sample?: Sample | null): Rate {
  return measured(sample, MIN_SAMPLE.hashRows, DEFAULT_RATES.hashFilesPerS);
}

/** Rows sent per second. */
export function metadataRate(sample?: Sample | null): Rate {
  return measured(
    sample,
    MIN_SAMPLE.metadataRows,
    1 / DEFAULT_RATES.metadataSPerPoint,
  );
}

/** Bytes uploaded per second. */
export function uploadRate(sample?: Sample | null): Rate {
  return measured(
    sample,
    MIN_SAMPLE.uploadBytes,
    DEFAULT_RATES.uploadBitsPerS / 8,
  );
}

/** Seconds left = remaining ÷ rate (never negative). */
export function remainingS(remaining: number, rate: Rate): number {
  if (remaining <= 0 || rate.perS <= 0) return 0;
  return remaining / rate.perS;
}

/** The ONE server job's whole estimate for `images`. */
export function serverJobS(images: number): number {
  return Math.max(0, images) * DEFAULT_RATES.geometrySPerImage;
}

/**
 * The server job's time left, counted down from its `startedAt`. Past the
 * estimate it reads 0 — the tile then shows the elapsed time instead of a
 * number that went negative.
 */
export function serverJobLeftS(
  images: number,
  startedAtMs: number | null,
  nowMs: number,
): number {
  const total = serverJobS(images);
  if (startedAtMs === null) return total;
  return Math.max(0, total - Math.max(0, (nowMs - startedAtMs) / 1000));
}

/** A set's ETA: the sum of its remaining stages; null when one of them is blocked. */
export function setEtaS(stages: readonly (number | null)[]): number | null {
  let sum = 0;
  for (const s of stages) {
    if (s === null) return null;
    sum += s;
  }
  return sum;
}

/** The drop's ETA: the max over the sets that have one (null when none has). */
export function dropEtaS(sets: readonly (number | null)[]): number | null {
  const xs = sets.filter((s): s is number => s !== null);
  return xs.length === 0 ? null : Math.max(...xs);
}

/** "about 31 min", "about 1 h 5 min" — a whole-set or whole-drop time. */
export function formatAbout(s: number): string {
  if (s < 60) return `about ${String(Math.max(1, Math.ceil(s)))} s`;
  const min = Math.round(s / 60);
  if (min < 60) return `about ${String(min)} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0
    ? `about ${String(h)} h`
    : `about ${String(h)} h ${String(m)} min`;
}

/** "20 s left", "3 min left" — a running tile's time. */
export function formatLeft(s: number): string {
  if (s < 60) return `${String(Math.max(1, Math.ceil(s)))} s left`;
  const min = Math.round(s / 60);
  if (min < 60) return `${String(min)} min left`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0
    ? `${String(h)} h left`
    : `${String(h)} h ${String(m)} min left`;
}

/** "31 min", "20 s" — the chip's short form ("on track · 31 min"). */
export function formatShort(s: number): string {
  if (s < 60) return `${String(Math.max(1, Math.ceil(s)))} s`;
  const min = Math.round(s / 60);
  if (min < 60) return `${String(min)} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${String(h)} h` : `${String(h)} h ${String(m)} min`;
}

/** "58 s", "2 min 40 s" — a finished stage's duration. */
export function formatDuration(s: number): string {
  const whole = Math.max(0, Math.round(s));
  if (whole < 60) return `${String(whole)} s`;
  const m = Math.floor(whole / 60);
  const r = whole % 60;
  return r === 0 ? `${String(m)} min` : `${String(m)} min ${String(r)} s`;
}

const UNITS = [
  { at: 1e9, unit: "GB" },
  { at: 1e6, unit: "MB" },
  { at: 1e3, unit: "kB" },
] as const;

function unitOf(bytes: number): { at: number; unit: string } {
  return UNITS.find((u) => bytes >= u.at) ?? { at: 1, unit: "B" };
}

function scaled(bytes: number, at: number): string {
  return (bytes / at).toFixed(at === 1 ? 0 : 1);
}

/** "6.3 GB", "40.0 MB" — decimal units, one decimal (board 2 / 3). */
export function formatBytes(bytes: number): string {
  const u = unitOf(Math.max(0, bytes));
  return `${scaled(Math.max(0, bytes), u.at)} ${u.unit}`;
}

/** "1.9 / 6.3 GB" — both in the total's unit (board 2's Upload tile). */
export function formatBytesPair(sent: number, total: number): string {
  const u = unitOf(Math.max(0, total));
  return `${scaled(Math.max(0, sent), u.at)} / ${scaled(Math.max(0, total), u.at)} ${u.unit}`;
}

/** "18 Mbit/s" — the status bar's rate (bytes per second in). */
export function formatMbit(bytesPerS: number): string {
  const mbit = (bytesPerS * 8) / 1e6;
  return `${mbit >= 10 ? mbit.toFixed(0) : mbit.toFixed(1)} Mbit/s`;
}
